import type { TagsQuery } from "@/generated/graphql";
import { useCreateTag } from "@/lib/hooks/tags/useCreateTag";
import { useUpdateTag } from "@/lib/hooks/tags/useUpdateTag";
import { Button, Checkbox, FormField, Modal } from "@ai-teach/ui-web";
import { useEffect, useState } from "react";

export type EditableTag = TagsQuery["tags"] extends (infer T)[] | null | undefined ? T : never;

const DEFAULT_TAG_COLOR = "#6366f1";

interface TagModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Omit to create. Passing a tag switches the modal to editing that tag. */
  tag?: EditableTag | null | undefined;
  onSaved?: (tag: EditableTag) => void;
}

export function TagModal({ isOpen, onClose, tag, onSaved }: TagModalProps) {
  const isEditing = Boolean(tag);

  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_TAG_COLOR);
  const [isArchived, setIsArchived] = useState(false);

  const { createTag, loading: creating } = useCreateTag();
  const { updateTag, loading: updating } = useUpdateTag();

  // The modal stays mounted between openings, so its fields are seeded from `tag` each time
  // rather than from initial state.
  useEffect(() => {
    if (!isOpen) return;
    setName(tag?.name ?? "");
    setColor(tag?.color ?? DEFAULT_TAG_COLOR);
    setIsArchived(tag?.isArchived ?? false);
  }, [isOpen, tag]);

  const isSaving = creating || updating;
  const canSubmit = Boolean(name.trim()) && !isSaving;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    // Left open on failure so the editor can fix a duplicate name without retyping.
    const saved = tag
      ? await updateTag(tag.id, { name: name.trim(), color, isArchived }).catch(() => null)
      : await createTag(name, color).catch(() => null);

    if (!saved) return;

    onSaved?.(saved);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? "Edit Tag" : "New Tag"} size="sm">
      <div className="space-y-4">
        <FormField
          id="tagName"
          name="tagName"
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Real Life Vocabulary"
          disabled={isSaving}
          {...(isEditing && {
            helpText:
              "Renaming keeps every slide already tagged — the underlying id never changes.",
          })}
        />

        <div>
          <label htmlFor="tagColor" className="block label">
            <span className="text-red-500 ml-1">* </span>Color
          </label>
          <div className="flex items-center gap-3 mt-1">
            <input
              id="tagColor"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              disabled={isSaving}
              className="w-10 h-10 p-0 bg-transparent border-none rounded-lg cursor-pointer"
            />
            <span
              className="h-9 px-4 rounded-full border label whitespace-nowrap flex items-center text-white"
              style={{ backgroundColor: color, borderColor: color }}
            >
              {name.trim() || "Preview"}
            </span>
          </div>
        </div>

        {isEditing && (
          <div className="flex items-center gap-2">
            <Checkbox checked={isArchived} onChange={setIsArchived} disabled={isSaving} size="sm" />
            <button
              type="button"
              onClick={() => setIsArchived(!isArchived)}
              className="label text-secondary-200"
            >
              Archived
            </button>
            <span className="caption text-gray-500">Hidden from pickers, kept on slides.</span>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-gray-800">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {isSaving ? "Saving..." : isEditing ? "Save Tag" : "Create Tag"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
